import NoImage, { NoImageBig } from '../../utils/noimage/noimage';
import UserIcon from '../../images/noun-image.svg';
import type { AuthorInfo, Config, ImageConfig, ImageSize, Item } from './image-path-creator.types';

const GERMAN_NO_IMAGE_PART = 'noimage-de';
const STATIC_MEDIA_PATH = '/static/media';
const IMG_SERVICE_HOST = process.env.REACT_APP_FRONT_URL || '';
const MAX_PREVIEW_HEIGHT = 500;
const DEFAULT_IMAGE_HEIGHT = 240;
const DEFAULT_IMAGE_QUALITY = 80;
const DEFAULT_AVATAR_SIZE = 80;
const WHITE_BACKGROUND = '255,255,255';

const imagePathCreator = {
    authorImage: (authorsInfo: AuthorInfo | undefined, size: ImageSize): string => {
        const { avatar_path, imagePath } = authorsInfo?.details ?? {};
        const url = avatar_path || imagePath;

        if (url) {
            const { w, h } = size;
            const config = { h: h || DEFAULT_AVATAR_SIZE, w: w || DEFAULT_AVATAR_SIZE };

            return imagePathCreator.resizedImg({ url }, config);
        }

        return UserIcon;
    },

    croppedImg: (item: { url: string }, config?: Config): string => {
        const { url } = item;
        const imageConfig: ImageConfig = {
            background: WHITE_BACKGROUND,
            type: 'webp',
            url
        };

        if (config) {
            applyConfig(imageConfig, config);
        }

        const queryParams = formQueryParams(capPreviewHeight(imageConfig));

        return `${IMG_SERVICE_HOST}/images/crop?${queryParams}`;
    },

    optimizedImg: (
        item: Item,
        config?: Config,
        shouldDisableOptimization = false,
        shouldUseBigPlaceholder = false
    ): string | typeof NoImage | typeof NoImageBig => {
        const { preview, imgUrl } = item;
        const url = getFirstPreviewImage(item, config) || preview || imgUrl;

        const hasCover = Boolean(url) && url !== '/preview-unavailable.png';
        const isMaterialNoImage = (url || '').includes(GERMAN_NO_IMAGE_PART);
        const shouldUsePlaceholder = !hasCover || isMaterialNoImage;

        if (shouldUsePlaceholder) {
            return shouldUseBigPlaceholder ? NoImageBig : NoImage;
        }

        const isUrlStaticImage = url?.includes(STATIC_MEDIA_PATH);
        const isNonDeLocale = !window.location.href.includes('.de');
        const shouldReturnOriginalUrl = shouldDisableOptimization || (isNonDeLocale && isUrlStaticImage);

        if (shouldReturnOriginalUrl) {
            return url;
        }

        const imageConfigUrl = resolveStaticUrl(url);
        const imageConfig: ImageConfig = {
            background: WHITE_BACKGROUND,
            height: DEFAULT_IMAGE_HEIGHT,
            quality: DEFAULT_IMAGE_QUALITY,
            type: 'webp',
            url: imageConfigUrl
        };

        if (config) {
            applyConfig(imageConfig, config);
        }

        if (imageConfigUrl) {
            const queryParams = formQueryParams(capPreviewHeight(imageConfig));

            return `${IMG_SERVICE_HOST}/images/convert?${queryParams}`;
        }

        return shouldUseBigPlaceholder ? NoImageBig : NoImage;
    },

    resizedImg: (item?: { url?: string }, config?: Config): string => {
        const { url } = item ?? {};
        const itemUrl = url ?? '';
        const isStaticMedia = itemUrl.includes(STATIC_MEDIA_PATH);

        if (!window.location.href.includes('.com') && isStaticMedia) {
            return itemUrl;
        }

        const imageConfig: ImageConfig = {
            height: DEFAULT_AVATAR_SIZE,
            nocrop: config?.nocrop,
            type: 'webp',
            url: resolveStaticUrl(url)
        };

        if (config) {
            applyConfig(imageConfig, config);
        }

        const queryParams = formQueryParams(capPreviewHeight(imageConfig));

        return `${IMG_SERVICE_HOST}/images/resize?${queryParams}`;
    }
};

const getFirstPreviewImage = (item: Item, config?: Config): string => {
    const { firstPreviewImage } = item;

    if (!firstPreviewImage) {
        return '';
    }

    return config?.watermarked ? firstPreviewImage.watermarked : firstPreviewImage.plain;
};

const applyConfig = (imageConfig: ImageConfig, config: Config): void => {
    const { w, h, q } = config;

    imageConfig.width = w || imageConfig.width;
    imageConfig.height = h || imageConfig.height;
    imageConfig.quality = q || imageConfig.quality || null;
};

const resolveStaticUrl = (url: string | undefined): string | undefined => {
    return url?.includes(STATIC_MEDIA_PATH) ? window.location.origin + url : url;
};

const capPreviewHeight = (imageConfig: ImageConfig): ImageConfig => {
    const { url, height = 0 } = imageConfig;

    if (url && url.includes('/previews')) {
        const numericHeight = typeof height === 'number' ? height : 0;
        const newHeight = Math.min(numericHeight, MAX_PREVIEW_HEIGHT) || MAX_PREVIEW_HEIGHT;

        return { ...imageConfig, height: newHeight };
    }

    return imageConfig;
};

const formQueryParams = (imageConfig: ImageConfig): string => {
    const keys = Object.keys(imageConfig);
    const queryParams: string[] = [];

    keys.forEach((key) => {
        const value = imageConfig[key as keyof ImageConfig];

        if (value === 'auto' || !value || key === 'url') {
            return;
        }

        queryParams.push(`${key}=${value}`);
    });

    const { url } = imageConfig;

    if (url) {
        queryParams.push(`url=${url}`);
    }

    return queryParams.join('&');
};

export default imagePathCreator;
